import React, { useCallback, useState, useRef } from "react";
import {
  Box,
  Text,
  Group,
  Button,
  Progress,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import { AppIcon } from "../AppIcon";

export interface FileUploadProps {
  /** Accepted file types (MIME types or extensions) */
  accept?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Whether multiple files are allowed */
  multiple?: boolean;
  /** Callback when files are selected */
  onFilesSelected?: (files: File[]) => void;
  /** Callback when files are dropped */
  onFilesDrop?: (files: File[]) => void;
  /** Callback for upload progress (0-100) */
  onUploadProgress?: (progress: number) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether upload is in progress */
  uploading?: boolean;
  /** Current upload progress (0-100) */
  uploadProgress?: number;
  /** Error message to display */
  error?: string | null;
  /** Success message to display */
  success?: string | null;
  /** Custom placeholder text */
  placeholder?: string;
  /** Custom description text */
  description?: string;
  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const FileUpload: React.FC<FileUploadProps> = ({
  accept = "*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  maxFiles = 1,
  multiple = false,
  onFilesSelected,
  onFilesDrop,
  disabled = false,
  uploading = false,
  uploadProgress = 0,
  error = null,
  success = null,
  placeholder = "Drop files here or click to browse",
  description = "Select files to upload",
  size = "md",
}) => {
  const theme = useMantineTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Size configurations
  const sizeConfig = {
    xs: { height: 80, iconSize: 16, spacing: "xs" },
    sm: { height: 100, iconSize: 18, spacing: "sm" },
    md: { height: 120, iconSize: 20, spacing: "md" },
    lg: { height: 140, iconSize: 24, spacing: "lg" },
    xl: { height: 160, iconSize: 28, spacing: "xl" },
  };

  const config = sizeConfig[size];

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Validate files
  const validateFiles = (
    files: FileList | File[],
  ): { valid: File[]; errors: string[] } => {
    const fileArray = Array.from(files);
    const valid: File[] = [];
    const errors: string[] = [];

    // Check file count
    if (fileArray.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`);
      return { valid, errors };
    }

    fileArray.forEach((file) => {
      // Check file size
      if (file.size > maxSize) {
        errors.push(
          `${file.name} is too large (max ${formatFileSize(maxSize)})`,
        );
        return;
      }

      // Check file type if specified
      if (accept !== "*") {
        const acceptedTypes = accept.split(",").map((type) => type.trim());
        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith(".")) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          return file.type.match(type);
        });

        if (!isAccepted) {
          errors.push(`${file.name} has unsupported file type`);
          return;
        }
      }

      valid.push(file);
    });

    return { valid, errors };
  };

  // Handle file selection from input
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const { valid, errors } = validateFiles(files);

      if (errors.length > 0) {
        // Could emit error callback here if needed
        return;
      }

      onFilesSelected?.(valid);

      // Clear input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFilesSelected, maxSize, maxFiles, accept],
  );

  // Handle drag events
  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && !uploading) {
        setIsDragOver(true);
      }
    },
    [disabled, uploading],
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (disabled || uploading) return;

      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;

      const { valid, errors } = validateFiles(files);

      if (errors.length > 0) {
        // Could emit error callback here if needed
        return;
      }

      onFilesDrop?.(valid);
    },
    [onFilesDrop, disabled, uploading, maxSize, maxFiles, accept],
  );

  // Handle browse button click
  const handleBrowseClick = useCallback(() => {
    if (!disabled && !uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, uploading]);

  // Dynamic styles based on state
  const getDropzoneStyles = () => {
    const baseStyles = {
      //height: config.height,
      border: `2px dashed ${theme.colors.gray[4]}`,
      borderRadius: theme.radius.md,
      backgroundColor: theme.colors.gray[0],
      cursor: disabled || uploading ? "not-allowed" : "pointer",
      transition: "all 150ms ease",
      padding: "20px",
    };

    if (disabled || uploading) {
      return {
        ...baseStyles,
        opacity: 0.6,
        backgroundColor: theme.colors.gray[1],
        border: `2px dashed ${theme.colors.gray[3]}`,
      };
    }

    if (isDragOver) {
      return {
        ...baseStyles,
        borderColor: theme.colors.blue[5],
        backgroundColor: theme.colors.blue[0],
      };
    }

    return baseStyles;
  };

  return (
    <Box>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={disabled || uploading}
      />

      {/* Drop zone */}
      <Box
        style={getDropzoneStyles()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <Group justify="center" align="center" h="100%" gap={config.spacing}>
          <Box ta="center">
            <AppIcon
              iconName={
                uploading ? "Loader2" : isDragOver ? "Upload" : "FileText"
              }
              size={config.iconSize}
              style={{
                color: isDragOver
                  ? theme.colors.blue[6]
                  : disabled || uploading
                    ? theme.colors.gray[5]
                    : theme.colors.gray[6],
                animation: uploading ? "spin 1s linear infinite" : undefined,
              }}
            />
            <Text
              size={size === "xs" ? "xs" : size === "sm" ? "sm" : "md"}
              fw={500}
              mt={4}
              c={
                isDragOver
                  ? theme.colors.blue[7]
                  : disabled || uploading
                    ? "dimmed"
                    : ""
              }
            >
              {uploading ? "Uploading..." : placeholder}
            </Text>
            <Text
              size={size === "xs" || size === "sm" ? "xs" : "sm"}
              c="dimmed"
              mt={2}
            >
              {uploading ? `${uploadProgress}% complete` : description}
            </Text>
            {!uploading && (
              <Button
                variant="light"
                size={size === "xs" ? "xs" : size === "sm" ? "sm" : "sm"}
                mt={size === "xs" ? 4 : 8}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
              >
                Browse Files
              </Button>
            )}
          </Box>
        </Group>
      </Box>

      {/* Upload progress */}
      {uploading && (
        <Progress
          value={uploadProgress}
          size="sm"
          mt="sm"
          color="blue"
          striped
          animated
        />
      )}

      {/* Error message */}
      {error && (
        <Alert
          color="red"
          mt="sm"
          icon={<AppIcon iconName="AlertCircle" size={16} />}
        >
          {error}
        </Alert>
      )}

      {/* Success message */}
      {success && (
        <Alert
          color="green"
          mt="sm"
          icon={<AppIcon iconName="CircleCheck" size={16} />}
        >
          {success}
        </Alert>
      )}

      {/* File size and type info */}
      {!uploading && !error && !success && (
        <Text size="xs" c="dimmed" mt="xs">
          {accept !== "*" && `Accepted: ${accept} • `}
          Max size: {formatFileSize(maxSize)}
          {maxFiles > 1 && ` • Max files: ${maxFiles}`}
        </Text>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};
